import React, { useState } from 'react';
import { X, Plus, Search, AlertTriangle } from 'lucide-react';
import { HEALTH_CONCERNS, HealthConcernCategory } from '../../shared/constants';
import { FormSection } from '../../shared/FormSection';
import SearchableDropdown from '../../shared/SearchableDropdown';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface HealthConcernsInputProps {
  healthConcerns: string[];
  setUserData: (callback: (prev: any) => any) => void;
  error?: string;
}

const HealthConcernsInput: React.FC<HealthConcernsInputProps> = ({ healthConcerns, setUserData, error }) => {
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleAddConcern = (concern: string) => {
    if (concern.trim() && !healthConcerns.includes(concern.trim())) {
      setUserData(prev => ({
        ...prev,
        healthConcerns: [...(prev.healthConcerns || []), concern.trim()]
      }));
      setInputValue('');
    }
  };

  const handleRemoveConcern = (concern: string) => {
    setUserData(prev => ({
      ...prev,
      healthConcerns: (prev.healthConcerns || []).filter(c => c !== concern)
    }));
  };

  return (
    <FormSection
      question="Do you have any existing health concerns?"
      helperText="We'll factor this in to make sure any recommendations we make are safe and effective for you."
      error={error}
    >
      <div className="space-y-4">


        <SearchableDropdown
          inputValue={inputValue}
          setInputValue={setInputValue}
          onSelect={handleAddConcern}
          categories={HEALTH_CONCERNS}
          placeholder="Search for a health concern..."
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />

<Alert variant="destructive" className="bg-amber-50/10 border-amber-800/20 text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            While this information will assist with making recommendations, our recommendations are not a substitute for professional medical advice.<br /><br />
            If you have any ongoing and untreated health concerns, you should talk to your doctor.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap gap-2">
          {healthConcerns.map((concern, index) => (
            <div 
              key={index}
              className="py-1.5 px-4 rounded-full flex items-center gap-2 bg-primary/5 text-sm"
            >
              <span>{concern}</span>
              <button 
                type="button"
                onClick={() => handleRemoveConcern(concern)}
                className="text-muted-foreground/60 hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </FormSection>
  );
};

export default HealthConcernsInput; 